/**
 * External Event Service for handling Twilio chat events and sending them to external APIs
 * Similar to AvayaController.handleExternalCryptEvent functionality
 */

import { initWasm, getClientCrypt, isWasmInitialized, type WasmCryptInstance } from '../utils/wasm/wasmLoader';

// Global crypt instance
let crypt: WasmCryptInstance | null = null;

export class ExternalEventService {
    private static instance: ExternalEventService;
    private baseUrl: string;
    private eventCryptAPIEndPoint: string;

    private constructor() {
        this.baseUrl = process.env.REACT_APP_TWILIO_API_URL || process.env.REACT_APP_CHATCON_URL || 'http://localhost:3010';
        this.eventCryptAPIEndPoint = process.env.REACT_APP_EVENT_API_ENDPOINT || 'https://api.example.com';

        console.log('🔧 EXTERNAL EVENT SERVICE INITIALIZED:', {
            baseUrl: this.baseUrl,
            eventCryptAPIEndPoint: this.eventCryptAPIEndPoint
        });
    }

    public static getInstance(): ExternalEventService {
        if (!ExternalEventService.instance) {
            ExternalEventService.instance = new ExternalEventService();
        }
        return ExternalEventService.instance;
    }

    /**
     * Handles External Crypt Events and sends them to the API
     * @param eventType - The type of event (e.g., 'PARTICIPANT_ADDED', 'MESSAGES', etc.)
     * @param providerDialogId - The ID of the provider dialog (conversationSid)
     * @param event - The event payload
     * @param socketEvent - The name of the socket event (for logging)
     */
    public async handleExternalCryptEvent(
        eventType: string, 
        providerDialogId: string, 
        event: any, 
        socketEvent: string
    ): Promise<void> {
        console.log(`🔐 HANDLING EXTERNAL CRYPT EVENT:`, {
            eventType,
            providerDialogId,
            socketEvent,
            event,
            timestamp: new Date().toISOString()
        });

        try {
            // Call the ExternalCryptEvents method
            await this.ExternalCryptEvents(eventType, event);
            
            console.log(`✅ EXTERNAL CRYPT EVENT PROCESSED SUCCESSFULLY:`, {
                eventType,
                providerDialogId,
                socketEvent
            });

        } catch (error: any) {
            console.error(`❌ ERROR IN EXTERNAL CRYPT EVENTS:`, {
                eventType,
                error: {
                    message: error.message,
                    stack: error.stack,
                    statusCode: error?.response?.status || 'Unknown',
                },
                timestamp: new Date().toISOString()
            });

            // Handle 401 Unauthorized error (retry logic)
            if (error?.response?.status === 401) {
                console.log('🔄 401 UNAUTHORIZED ERROR - RETRYING...');

                try {
                    // Refresh the token and retry
                    await this.RefreshToken();
                    await this.ExternalCryptEvents(eventType, event);

                    console.log(`✅ RETRY SUCCESSFUL FOR EVENT:`, { eventType });
                } catch (retryError: any) {
                    console.error(`❌ RETRY FAILED FOR EVENT:`, {
                        eventType,
                        retryError: retryError.message
                    });

                    throw new Error(`Failed to process ${eventType} event after retrying.`);
                }
            } else {
                // Rethrow the error for any other status code
                throw error;
            }
        }
    }

    /**
     * Sends encrypted event data to external API
     * @param type - Event type
     * @param event - Event data
     */
    
    /**
    private async ExternalCryptEvents(type: string, event: any): Promise<any> {
        console.log(`🔐 SENDING ENCRYPTED EVENT:`, { type, event });

    try {
        if (!event || typeof event !== 'object') {
            throw new Error('Invalid event data: must be a non-null object');
        }

        const fetchedCrypt = await this.FetchCrypt();
        const eventJson = JSON.stringify(event);

        // --- UTF-8 safe Base64 encoding ---
        const utf8ToBase64 = (str: string) =>
            window.btoa(unescape(encodeURIComponent(str)));
        const base64ToUtf8 = (str: string) =>
            decodeURIComponent(escape(window.atob(str)));

        const utf8Encoded = utf8ToBase64(eventJson);

        const encrypted_request = fetchedCrypt.encrypt(utf8Encoded);
        console.log(`🔐 ENCRYPTED REQUEST:`, encrypted_request);

        const response = await fetch(
            `${this.eventCryptAPIEndPoint}/proxy/ccaas/event-type/chat_connector_subscription`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "User-Agent": "Twilio-Chat-Client/1.0",
                },
                body: encrypted_request,
            }
        );

        console.log(`📡 API RESPONSE STATUS:`, response.status, response.statusText);

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`❌ API ERROR RESPONSE:`, {
                status: response.status,
                statusText: response.statusText,
                body: errorText
            });
            throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const responseText = await response.text();
        const decrypted = fetchedCrypt.decrypt(responseText);

        // --- decode UTF-8 safely ---
        const result = JSON.parse(base64ToUtf8(decrypted));
        console.log(`✅ DECRYPTED RESPONSE:`, { type, result });

        return { hasError: 0, result };

        } catch (error: any) {
            console.error(`❌ ERROR SENDING ENCRYPTED EVENT:`, {
                type,
                error: error.message,
                timestamp: new Date().toISOString()
            });

            throw error;
        }
    }
    */
    
    
    private async ExternalCryptEvents(type: string, event: any): Promise<any> {
        console.log(`🔐 SENDING PLAIN EVENT (NO ENCRYPTION):`, { type, event });

        try {
            // Validate event data
            if (!event || typeof event !== "object") {
                throw new Error("Invalid event data: must be a non-null object");
            }

            // Automatically determine eventType
            const eventType = event.eventType || type || "UNKNOWN";
            
            // Build the payload in required array format
            const payload = [
                type,
                {
                    eventType,
                    ...event
                }
            ];

            console.log(`📝 FINAL REQUEST PAYLOAD:`, JSON.stringify(payload, null, 2));

            // Send POST request to external API
            const response = await fetch(
                `${this.baseUrl}/chat_connector_subscription`,
                {
                    method: "POST",
                    headers: {
                        "origin": "http://localhost:3003",
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        "User-Agent": "Twilio-Chat-Client/1.0",
                    },
                    body: JSON.stringify(payload),
                }
            );

            console.log(`📡 API RESPONSE STATUS:`, response.status, response.statusText);
            console.log(`📡 API RESPONSE HEADERS:`, Object.fromEntries(response.headers.entries()));

            const responseText = await response.text();
            console.log(`📡 RAW RESPONSE BODY:`, responseText);

            if (!response.ok) {
                console.error(`❌ API ERROR RESPONSE:`, {
                    status: response.status,
                    statusText: response.statusText,
                    body: responseText
                });
                throw new Error(`API request failed: ${response.status} ${response.statusText} - ${responseText}`);
            }

            // Parse JSON response safely
            let parsedResponse: any = null;
            try {
                parsedResponse = JSON.parse(responseText);
            } catch {
                console.warn("⚠️ Response is not JSON, returning raw text");
                parsedResponse = responseText;
            }

            console.log(`✅ EVENT SENT SUCCESSFULLY:`, { type, eventType, parsedResponse });
            return { hasError: 0, result: parsedResponse };

        } catch (error: any) {
            console.error(`❌ ERROR SENDING PLAIN EVENT:`, {
                type,
                error: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString()
            });
            throw error;
        }
    }
    

    /**
     * Fetches or initializes the crypt instance using WASM
     */
    private async FetchCrypt(): Promise<WasmCryptInstance> {
        try {
            if (!crypt) {
                console.log('🔑 INITIALIZING WASM CRYPT INSTANCE...');

                // Initialize WASM if not already done
                if (!isWasmInitialized()) {
                    await initWasm();
                }

                // Fetch the encryption key
                const response = await fetch(`${this.eventCryptAPIEndPoint}/proxy/ccaas/get-key`);
                const keyText = await response.text();

                // Create ClientCrypt instance with the key
                const ClientCrypt = getClientCrypt();
                crypt = new ClientCrypt(keyText);

                console.log('✅ WASM CRYPT INSTANCE INITIALIZED');
            }
            if (!crypt) {
                throw new Error('Failed to initialize crypt instance');
            }
            return crypt;
        } catch (error: any) {
            console.error('❌ ERROR INITIALIZING CRYPT:', error);
            throw new Error(`Failed to initialize crypt: ${error.message}`);
        }
    }

    /**
     * Refreshes authentication token
     */
    private async RefreshToken(): Promise<void> {
        try {
            console.log('🔄 REFRESHING TOKEN...');

            const response = await fetch(`${this.baseUrl}/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Token refresh failed: ${response.status}`);
            }

            const data = await response.json();
            console.log('✅ TOKEN REFRESHED SUCCESSFULLY:', data);

            // Store the new token if needed
            // localStorage.setItem('authToken', data.token);

        } catch (error: any) {
            console.error('❌ TOKEN REFRESH FAILED:', error);
            throw error;
        }
    }

    /**
     * Common method to handle different event types
     * @param eventType - Type of event
     * @param eventData - Event data
     * @param conversationSid - Conversation ID
     */
    public async processEvent(eventType: string, eventData: any, conversationSid: string): Promise<void> {
        console.log(`🎯 PROCESSING EVENT:`, { eventType, conversationSid, eventData });

        switch (eventType) {
            case 'PARTICIPANT_ADDED':
                await this.handleExternalCryptEvent('PARTICIPANT_ADDED', conversationSid, eventData, 'participantAdded');
                break;

            case 'PARTICIPANT_DISCONNECTED':
                await this.handleExternalCryptEvent('PARTICIPANT_DISCONNECTED', conversationSid, eventData, 'participantDisconnected');
                break;

            case 'MESSAGES':
                await this.handleExternalCryptEvent('MESSAGES', conversationSid, eventData, 'message');
                break;

            case 'ENGAGEMENT_CREATED':
                await this.handleExternalCryptEvent('ENGAGEMENT_CREATED', conversationSid, eventData, 'engagementCreated');
                break;

            default:
                console.warn(`⚠️ UNKNOWN EVENT TYPE:`, { eventType, conversationSid });
                break;
        }
    }
}

// Export singleton instance
export const externalEventService = ExternalEventService.getInstance();
